import React, { useState, useEffect, useMemo } from "react";
import {
	Control,
	Controller,
	FieldErrors,
	UseFormSetValue,
	UseFormGetValues,
	useWatch,
} from "react-hook-form";
import BasicSelect from "../BasicSelect";
import {
	CurrentTreatmentPlan,
	EnrollmentData,
	Program,
	TreatmentConfig,
} from "../../domain/beneficiary.interface";
import { TreatmentConfigTemplate } from "../../domain/treatmentPlan.interface";
import { TextField, Box } from "@mui/material";
import { useGetTreatmentPlans } from "../../hooks/useTreatmentPlans";
import { useGetEvents } from "../../hooks/useEvents";
import { useRewardsEngine } from "../../hooks/useRewardsEngine";
import { EnrollmentStatus } from "../../domain/enrollment.interface";
import {
	hasSegments,
	readTemplateSegments,
} from "../SegmentEditor/segmentEditor.helpers";

interface TreatmentPlanFieldsProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control: Control<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	errors: FieldErrors<any>;
	selectedProgram?: Program;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setValue: UseFormSetValue<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getValues: UseFormGetValues<any>;
	isEditMode?: boolean; // Flag to differentiate between register and edit flows
	isReenrollMode?: boolean; // Flag specifically for reenroll modal styling
	currentTreatmentPlan?: CurrentTreatmentPlan;
	enrollment?: EnrollmentData;
	allowCmVisitScheduleEdit?: boolean; // Setting to allow CM visit schedule editing even when treatmentPlanChangeAllowed is false
}

const TreatmentPlanFields: React.FC<TreatmentPlanFieldsProps> = ({
	control,
	errors,
	selectedProgram,
	setValue,
	getValues,
	isEditMode = false, // Default to false (register mode)
	isReenrollMode = false, // Default to false
	currentTreatmentPlan, // Current treatment plan from beneficiary data
	enrollment,
	allowCmVisitScheduleEdit = false,
}) => {
	const {
		data: treatmentPlans,
		isFetching: isFetchingPlans,
		isLoading: isLoadingPlans,
	} = useGetTreatmentPlans();
	const {
		data: events,
		isFetching: isFetchingEvents,
		isLoading: isLoadingEvents,
	} = useGetEvents();
	// The reward engines a custom plan can override to. Same source as the admin template editor
	// (TreatmentPlanForm) — the plan's own `treatmentConfigOptions.rewardEngineIdsOptions` is not
	// populated in practice, which is why reading it offered nothing but "None".
	const { data: rewardEngines } = useRewardsEngine(true);

	const rewardEngineChoices = useMemo(
		() =>
			rewardEngines
				?.filter((engine) => engine._id)
				.map((engine) => ({
					key: engine._id as string,
					// Mirrors the editor's label so the same engine reads the same in both places.
					value: `${engine.eventType} - ${engine.event}`,
				})) || [],
		[rewardEngines],
	);

	// UI state for period length and user-friendly duration
	const [treatmentPeriodicity, setTreatmentPeriodicity] = useState<
		"weekly" | "monthly"
	>("weekly");
	const [userDuration, setUserDuration] = useState<number>(0);
	const [isSettingFromTemplate, setIsSettingFromTemplate] = useState(false);

	// Helper function to convert duration to weeks
	const convertDurationToWeeks = (
		duration: number,
		periodicity: "weekly" | "monthly",
	): number => {
		if (periodicity === "weekly") {
			return duration; // Already in weeks
		} else {
			// Convert months to weeks using more precise calculation
			// Average month = 30.4375 days, 30.4375 / 7 = 4.348 weeks per month
			const monthsInWeeks = Math.round(duration * 4.348);
			return monthsInWeeks;
		}
	};

	// Update backend duration when UI values change
	useEffect(() => {
		// Skip conversion if we're setting from template
		if (isSettingFromTemplate) {
			return;
		}

		if (userDuration > 0) {
			const durationInWeeks = convertDurationToWeeks(
				userDuration,
				treatmentPeriodicity,
			);
			setValue("treatmentConfig.duration", durationInWeeks);
		}
	}, [userDuration, treatmentPeriodicity, setValue, isSettingFromTemplate]);

	// Sync display state with form values on mount or when form values change
	// This ensures UI stays in sync after tab switches or data refetches
	useEffect(() => {
		const currentDuration = getValues("treatmentConfig.duration");
		const currentPeriodLength = getValues("treatmentConfig.periodLength");

		if (currentDuration && currentPeriodLength) {
			let periodicity: "weekly" | "monthly" = "weekly";

			// Determine periodicity from period length
			if (typeof currentPeriodLength === "number") {
				periodicity = currentPeriodLength === 4.348 ? "monthly" : "weekly";
			} else if (typeof currentPeriodLength === "string") {
				periodicity = currentPeriodLength as "weekly" | "monthly";
			}

			setTreatmentPeriodicity(periodicity);

			// Set flag to prevent useEffect from re-converting
			setIsSettingFromTemplate(true);

			// Convert display value based on periodicity
			if (periodicity === "monthly") {
				const durationInMonths = Math.round(currentDuration / 4.348);
				setUserDuration(durationInMonths);
			} else {
				setUserDuration(currentDuration);
			}
		}
	}, [
		selectedProgram?.treatmentPlan,
		treatmentPlans,
		getValues,
		isFetchingPlans,
	]);

	// Helper function to find matching CM visit schedule option
	const findMatchingScheduleOption = (currentScheduleDays: string) => {
		if (
			!associatedTreatmentPlan?.treatmentConfigOptions?.cmVisitScheduleOptions
		) {
			return null;
		}

		const availableOptions =
			associatedTreatmentPlan.treatmentConfigOptions.cmVisitScheduleOptions;

		// Simply match by cmVisitScheduleOptionsDays
		return availableOptions.find(
			(option) => option.cmVisitScheduleOptionsDays === currentScheduleDays,
		);
	};

	// Find the treatment plan associated with the selected program
	const associatedTreatmentPlan = useMemo(() => {
		if (!selectedProgram?.treatmentPlan || !treatmentPlans) return null;
		return treatmentPlans.find(
			(tp) => tp._id === selectedProgram.treatmentPlan,
		);
	}, [selectedProgram?.treatmentPlan, treatmentPlans]);

	// Convert treatment config templates to options format
	const treatmentConfigTemplateOptions = useMemo(() => {
		const templates =
			associatedTreatmentPlan?.treatmentConfigOptions
				?.treatmentConfigTemplates || [];
		const options: Array<{
			key: string;
			value: string;
			data: TreatmentConfigTemplate | null;
		}> = templates.map((template, index) => ({
			// SIMS-9041: the key is stored on the enrollment as its link back to this template, so
			// it must be a stable IDENTITY. It used to be `template_${index}` — a position in the
			// rendered list, which is not identity: it survives a rename by luck and breaks
			// silently the moment templates are reordered or one is deleted.
			//
			// Plans saved before templates had an `_id` still fall back to the positional token, so
			// those enrollments keep resolving by name (see beneficiaries.service.ts) until the
			// plan is next saved.
			key: (template as { _id?: string })._id ?? `template_${index}`,
			value: template.name || `Template ${index + 1}`,
			data: template,
		}));

		// Add custom plan option if allowed
		if (associatedTreatmentPlan?.customPlanAllowed) {
			options.push({
				key: "custom",
				value: "Custom Plan",
				data: null, // No template data for custom plan
			});
		}

		return options;
	}, [
		associatedTreatmentPlan?.treatmentConfigOptions?.treatmentConfigTemplates,
		associatedTreatmentPlan?.customPlanAllowed,
	]);

	// State to track selected template
	const [selectedTreatmentConfigTemplate, setSelectedTreatmentConfigTemplate] =
		useState<string>("");

	// Watch the form value for template selection
	const watchedTemplate = useWatch({
		control,
		name: "treatmentConfigTemplate",
	});

	// Watched only to drive the CUSTOM-plan derivations below.
	const watchedVisitsPerPeriod = useWatch({
		control,
		name: "treatmentConfig.visitsPerPeriod",
	});

	// Sync local state with form state when form value changes
	useEffect(() => {
		if (
			watchedTemplate &&
			watchedTemplate !== selectedTreatmentConfigTemplate
		) {
			setSelectedTreatmentConfigTemplate(watchedTemplate);
		}
	}, [watchedTemplate, selectedTreatmentConfigTemplate]);

	// Restore template selection from form after tab switch or refetch
	useEffect(() => {
		const currentTemplate = getValues("treatmentConfigTemplate");
		if (
			currentTemplate &&
			currentTemplate !== selectedTreatmentConfigTemplate &&
			!isFetchingPlans
		) {
			setSelectedTreatmentConfigTemplate(currentTemplate);
		}
	}, [
		getValues,
		selectedTreatmentConfigTemplate,
		isFetchingPlans,
		treatmentPlans,
	]);

	// Reset template selection when program changes
	useEffect(() => {
		// Only clear if we don't already have a saved value (i.e., actual program change, not tab switch)
		const currentTemplate = getValues("treatmentConfigTemplate");
		const currentSchedule = getValues("treatmentConfig.cmVisitSchedule");

		if (!currentTemplate) {
			setSelectedTreatmentConfigTemplate("");
		}

		// Only clear CM visit schedule if there's no saved value
		if (!currentSchedule) {
			setValue("treatmentConfig.cmVisitSchedule", "");
		}
	}, [selectedProgram?.treatmentPlan, setValue, getValues]);

	// Auto-populate CM visit schedule in edit mode
	useEffect(() => {
		if (
			(isEditMode || isReenrollMode) &&
			currentTreatmentPlan?.treatmentConfig?.cmVisitSchedule
				?.cmVisitScheduleOptionsDays &&
			associatedTreatmentPlan?.treatmentConfigOptions?.cmVisitScheduleOptions
		) {
			const currentScheduleDays =
				currentTreatmentPlan.treatmentConfig.cmVisitSchedule
					.cmVisitScheduleOptionsDays;
			const matchingOption = findMatchingScheduleOption(currentScheduleDays);
			if (matchingOption) {
				setValue("treatmentConfig.cmVisitSchedule", matchingOption);
			}
		}
	}, [
		isEditMode,
		isReenrollMode,
		currentTreatmentPlan?.treatmentConfig?.cmVisitSchedule
			?.cmVisitScheduleOptionsDays,
		associatedTreatmentPlan?.treatmentConfigOptions?.cmVisitScheduleOptions,
		setValue,
	]);

	// Auto-select matching template when editing based on current treatment config
	useEffect(() => {
		if (
			(isEditMode || isReenrollMode) &&
			currentTreatmentPlan?.treatmentConfig &&
			treatmentConfigTemplateOptions.length > 0
		) {
			const config = currentTreatmentPlan.treatmentConfig;

			// Find matching template by comparing key values
			const matchingTemplate = treatmentConfigTemplateOptions.find((option) => {
				if (!option.data || option.key === "custom") return false;

				const templateData = option.data as TreatmentConfigTemplate;

				// Compare key fields to find the best match
				const durationMatch = templateData.duration === config.duration;
				const visitsMatch =
					templateData.visitsPerPeriod === config.visitsPerPeriod;
				const hoursMatch =
					templateData.minHoursBetweenVisits === config.minHoursBetweenVisits;
				const periodMatch = templateData.periodLength === config.periodLength;

				// Compare schedule option IDs directly
				let scheduleMatch = false;

				// Check if both have cmVisitScheduleOptionIds
				if (
					templateData.cmVisitScheduleOptionIds &&
					config.cmVisitScheduleOptionIds
				) {
					// Normalize both to arrays for comparison
					const templateIds = Array.isArray(
						templateData.cmVisitScheduleOptionIds,
					)
						? templateData.cmVisitScheduleOptionIds
						: templateData.cmVisitScheduleOptionIds
								.toString()
								.split(",")
								.map((id) => id.trim());

					const configIds = Array.isArray(config.cmVisitScheduleOptionIds)
						? config.cmVisitScheduleOptionIds
						: config.cmVisitScheduleOptionIds
								.toString()
								.split(",")
								.map((id) => id.trim());

					// Sort both arrays to compare regardless of order
					const sortedTemplateIds = [...templateIds].sort();
					const sortedConfigIds = [...configIds].sort();

					// They match if the arrays contain the same IDs
					scheduleMatch =
						sortedTemplateIds.length === sortedConfigIds.length &&
						sortedTemplateIds.every(
							(id, index) => id === sortedConfigIds[index],
						);
				} else {
					// If either doesn't have schedule option IDs, they match if both are undefined/null
					scheduleMatch =
						!templateData.cmVisitScheduleOptionIds &&
						!config.cmVisitScheduleOptionIds;
				}

				const fieldsMatch =
					durationMatch &&
					visitsMatch &&
					hoursMatch &&
					periodMatch &&
					scheduleMatch;

				// Also check behavior field - could be array or string in template
				let behaviorMatches = true; // Default to true if no behavior to check
				if (config.behaviorEntity?._id || config.behavior) {
					const currentBehaviorId =
						config.behaviorEntity?._id || config.behavior;
					if (Array.isArray(templateData.behavior)) {
						behaviorMatches = templateData.behavior.includes(currentBehaviorId);
					} else {
						behaviorMatches = templateData.behavior === currentBehaviorId;
					}
				}

				const overallMatch = fieldsMatch && behaviorMatches;

				return overallMatch;
			});

			if (matchingTemplate) {
				// Auto-select the matching template
				setSelectedTreatmentConfigTemplate(matchingTemplate.key);
				setValue("treatmentConfigTemplate", matchingTemplate.key);

				// Auto-populate fields from the selected template (inline logic)
				if (matchingTemplate.data && matchingTemplate.key !== "custom") {
					const templateData = matchingTemplate.data as unknown as Record<
						string,
						unknown
					>;

					// Set each field from the template data (except cmVisitSchedule)
					Object.keys(templateData).forEach((key) => {
						// Skip cmVisitSchedule - it's handled by dedicated useEffect
						if (key === "cmVisitSchedule") return;

						let value = templateData[key];

						// Special handling for behavior field - convert array to single string
						if (key === "behavior" && Array.isArray(value)) {
							value = value.length > 0 ? value[0] : "";
						}

						setValue(`treatmentConfig.${key}`, value);
					});

					// Ensure weeklyVisitsPerPeriod matches visitsPerPeriod if not explicitly set in template
					const currentWeeklyVisits = getValues(
						"treatmentConfig.weeklyVisitsPerPeriod",
					);
					const currentVisitsPerPeriod = getValues(
						"treatmentConfig.visitsPerPeriod",
					);
					if (!currentWeeklyVisits && currentVisitsPerPeriod) {
						setValue(
							"treatmentConfig.weeklyVisitsPerPeriod",
							currentVisitsPerPeriod,
						);
					}

					// Note: CM visit schedule is handled by the dedicated useEffect above
				}
			} else {
				// No matching template found, might be a custom configuration
				if (associatedTreatmentPlan?.customPlanAllowed) {
					setSelectedTreatmentConfigTemplate("custom");
					setValue("treatmentConfigTemplate", "custom");
					// Manually populate the custom fields from current config
					populateFieldsFromCurrentConfig(config);
				}
			}
		}
	}, [
		isEditMode,
		isReenrollMode,
		currentTreatmentPlan,
		treatmentConfigTemplateOptions,
		associatedTreatmentPlan?.customPlanAllowed,
		associatedTreatmentPlan?.treatmentConfigOptions?.cmVisitScheduleOptions,
		setValue,
		getValues,
	]);

	// Helper function to populate fields from current config (for custom plans)
	const populateFieldsFromCurrentConfig = (
		config: TreatmentConfig & { minHoursBetweenVisits?: number },
	) => {
		// Map direct fields
		if (config.rewardEngineId) {
			setValue("treatmentConfig.rewardEngineId", config.rewardEngineId);
		}
		if (config.visitsPerPeriod) {
			setValue("treatmentConfig.visitsPerPeriod", config.visitsPerPeriod);
			setValue("treatmentConfig.weeklyVisitsPerPeriod", config.visitsPerPeriod);
		}
		if (config.duration) {
			setValue("treatmentConfig.duration", config.duration);
		}
		if (config.periodLength) {
			setValue("treatmentConfig.periodLength", config.periodLength);
		}
		if (config.minHoursBetweenVisits) {
			setValue(
				"treatmentConfig.minHoursBetweenVisits",
				config.minHoursBetweenVisits,
			);
		}

		// Map behavior
		if (config.behaviorEntity?._id) {
			setValue("treatmentConfig.behavior", config.behaviorEntity._id);
		} else if (config.behavior) {
			setValue("treatmentConfig.behavior", config.behavior);
		}

		// Note: CM visit schedule is handled by the dedicated useEffect above
		if (config.cmVisitScheduleOptionIds) {
			setValue(
				"treatmentConfig.cmVisitScheduleOptionIds",
				config.cmVisitScheduleOptionIds,
			);
		}

		// Handle duration display conversion
		if (config.duration && typeof config.duration === "number") {
			const durationInWeeks = config.duration;
			let periodicity: "weekly" | "monthly" = "weekly";

			if (config.periodLength) {
				if (typeof config.periodLength === "number") {
					periodicity = config.periodLength === 4.348 ? "monthly" : "weekly";
				} else if (typeof config.periodLength === "string") {
					periodicity = config.periodLength as "weekly" | "monthly";
				}
			}

			setTreatmentPeriodicity(periodicity);

			if (periodicity === "monthly") {
				const durationInMonths = Math.round(durationInWeeks / 4.348);
				setUserDuration(durationInMonths);
			} else {
				setUserDuration(durationInWeeks);
			}
		}
	};

	// Check if fields should be editable
	const isCustomPlanSelected = selectedTreatmentConfigTemplate === "custom";

	const areFieldsEditable =
		associatedTreatmentPlan?.customPlanAllowed && isCustomPlanSelected;

	/**
	 * SIMS-7491 (FR-14) — a custom plan MAY edit the structure, including on a segmented plan.
	 *
	 * The service layer refuses a structural custom override on a segmented plan
	 * (`validateNoStructuralOverride`), and the deliberate choice here is to let the user author one
	 * and be told why it was refused, rather than to disable the fields. The refusal names the
	 * offending field and says the segments define the structure, which is more use than a greyed-out
	 * box with no explanation — and it keeps the guard's behaviour visible instead of hidden behind
	 * the form.
	 *
	 * What makes that workable is the seeding in handleTemplateSelection: on a segmented plan the
	 * structural fields start at the plan's own values, so they ECHO and the default path succeeds.
	 * Only a deliberate change produces the 400.
	 *
	 * Predefined templates are untouched: `areFieldsEditable` was already false for them.
	 */
	// SIMS-9222: the segments are the SELECTED template's own list, else the plan-level list a plan
	// saved before that change carries — the same rule the backend snapshots with.
	const planHasSegments = hasSegments(
		readTemplateSegments(
			associatedTreatmentPlan?.treatmentConfig as
				| Record<string, unknown>
				| undefined,
			treatmentConfigTemplateOptions.find(
				(option) => option.key === watchedTemplate,
			)?.data ?? undefined,
		),
	);

	/**
	 * Keep the derived TreatmentConfig fields in step while a CUSTOM plan is selected.
	 *
	 * `periodLength` is the numeric form of the Period Length selector (1 weekly / 4.348 monthly) and
	 * is required by the enrollment schema, but the selector only drives UI state — nothing wrote the
	 * numeric value. `range` is the week band, so it follows the duration.
	 *
	 * Scoped to custom on purpose: a predefined template supplies both, and this must not touch them.
	 */
	useEffect(() => {
		if (!isCustomPlanSelected) {
			return;
		}
		setValue(
			"treatmentConfig.periodLength",
			treatmentPeriodicity === "monthly" ? 4.348 : 1,
		);
		const weeks = convertDurationToWeeks(userDuration, treatmentPeriodicity);
		if (weeks > 0) {
			setValue("treatmentConfig.range", `1-${weeks}`);
		}

		// `weeklyVisitsPerPeriod` is required by the enrollment schema but has NO control in this
		// modal: a predefined template carries it, a hand-built custom config has nothing to carry it,
		// and the enrollment 500'd on the missing path. On a weekly plan it is the same figure as
		// visits-per-period, so it is mirrored here rather than normalised on the shared payload —
		// keeping templated enrollments byte-identical to before.
		if (
			watchedVisitsPerPeriod &&
			getValues("treatmentConfig.weeklyVisitsPerPeriod") !==
				watchedVisitsPerPeriod
		) {
			setValue("treatmentConfig.weeklyVisitsPerPeriod", watchedVisitsPerPeriod);
		}
	}, [
		isCustomPlanSelected,
		treatmentPeriodicity,
		userDuration,
		watchedVisitsPerPeriod,
		getValues,
		setValue,
	]);

	// Helper function for styling - only edit mode should have no margins
	const getFieldStyling = (additionalSx = {}) => {
		const baseMargins = { mb: 1, mt: 1 };
		// Only edit mode (not reenroll) should have empty styling
		return isEditMode && !isReenrollMode
			? { ...additionalSx }
			: { ...baseMargins, ...additionalSx };
	};

	// Handle template selection and auto-populate fields
	const handleTemplateSelection = (templateKey: string) => {
		// Update selected template state
		setSelectedTreatmentConfigTemplate(templateKey);

		const selectedTemplate = treatmentConfigTemplateOptions.find(
			(option) => option.key === templateKey,
		);

		if (
			selectedTemplate &&
			selectedTemplate.data &&
			selectedTemplate.key !== "custom"
		) {
			// Auto-populate fields from the selected template
			const templateData = selectedTemplate.data as unknown as Record<
				string,
				unknown
			>;

			// First, clear the CM visit schedule to avoid showing stale data
			setValue("treatmentConfig.cmVisitSchedule", "");

			// Set each field from the template data
			Object.keys(templateData).forEach((key) => {
				// Skip cmVisitSchedule - we'll handle it separately after clearing
				if (key === "cmVisitSchedule") {
					return;
				}

				let value = templateData[key];

				// Special handling for behavior field - convert array to single string
				if (key === "behavior" && Array.isArray(value)) {
					value = value.length > 0 ? value[0] : "";
				}

				setValue(`treatmentConfig.${key}`, value);
			});

			// After populating other fields, handle CM visit schedule
			// Get the template's schedule option IDs
			const templateScheduleIds = (
				templateData as unknown as TreatmentConfigTemplate
			).cmVisitScheduleOptionIds;

			if (
				templateScheduleIds &&
				associatedTreatmentPlan?.treatmentConfigOptions?.cmVisitScheduleOptions
			) {
				// Convert to array if it's a string
				const scheduleIdsArray = Array.isArray(templateScheduleIds)
					? templateScheduleIds
					: templateScheduleIds
							.toString()
							.split(",")
							.map((id) => id.trim());

				// Find the available schedule options for this template
				const availableSchedules =
					associatedTreatmentPlan.treatmentConfigOptions.cmVisitScheduleOptions.filter(
						(opt) =>
							scheduleIdsArray.includes(opt.cmVisitScheduleOptions.toString()),
					);

				// If there are available schedules, set the first one as default
				if (availableSchedules.length > 0) {
					const defaultSchedule = availableSchedules[0];
					setValue("treatmentConfig.cmVisitSchedule", defaultSchedule);
				}
			}

			// Ensure weeklyVisitsPerPeriod matches visitsPerPeriod if not explicitly set in template
			const currentWeeklyVisits = getValues(
				"treatmentConfig.weeklyVisitsPerPeriod",
			);
			const currentVisitsPerPeriod = getValues(
				"treatmentConfig.visitsPerPeriod",
			);
			if (!currentWeeklyVisits && currentVisitsPerPeriod) {
				setValue(
					"treatmentConfig.weeklyVisitsPerPeriod",
					currentVisitsPerPeriod,
				);
			}

			// Note: CM visit schedule is handled by the dedicated useEffect above

			// Update UI state based on template duration and period length
			if (templateData.duration && typeof templateData.duration === "number") {
				const durationInWeeks = templateData.duration;

				// Use period length from template, default to weekly if not specified
				const rawPeriodLength = (
					templateData as unknown as TreatmentConfigTemplate
				).periodLength;
				let templatePeriodicity: "weekly" | "monthly" = "weekly";

				// Handle both numeric (backend) and string (frontend) period length values
				if (typeof rawPeriodLength === "number") {
					// Convert numeric values back to display strings
					templatePeriodicity =
						rawPeriodLength === 4.348 ? "monthly" : "weekly";
				} else if (typeof rawPeriodLength === "string") {
					// Use string values directly
					templatePeriodicity = rawPeriodLength as "weekly" | "monthly";
				}

				// Always store as weeks in the form FIRST (backend expects weeks)
				setValue("treatmentConfig.duration", durationInWeeks);

				// Set flag to prevent useEffect from re-converting
				setIsSettingFromTemplate(true);

				setTreatmentPeriodicity(templatePeriodicity);

				// Convert duration display based on period length
				if (templatePeriodicity === "monthly") {
					// Convert weeks to months for display (using 4.348 weeks per month)
					const durationInMonths = Math.round(durationInWeeks / 4.348);
					setUserDuration(durationInMonths);
				} else {
					// Show duration as-is for weekly
					setUserDuration(durationInWeeks);
				}
			}
		} else if (selectedTemplate?.key === "custom") {
			// A custom plan lets the user choose the NON-structural configuration.
			setValue("treatmentConfig.behavior", "");
			setValue("treatmentConfig.cmVisitSchedule", "");
			// Left UNDEFINED rather than "" — the payload spreads treatmentConfig straight through
			// and Mongoose cannot cast an empty string to an ObjectId ("Cast to ObjectId failed for
			// value \"\""), which 500'd the whole registration.
			setValue("treatmentConfig.rewardEngineId", undefined);
			setValue("treatmentConfig.cmVisitScheduleOptionIds", "");
			setValue("treatmentConfig.weeklyVisitsPerPeriod", "");

			// The STRUCTURAL fields depend on whether the plan is segmented (SIMS-7491 FR-14).
			//
			// Segmented: the segments own the structure and the service layer rejects any structural
			// value that is not an echo of the plan's own. Seed them from the plan's first config
			// template — the same source the guard compares against — and render them read-only, so
			// the form cannot author something that will come back as a 400.
			//
			// Not segmented: nothing guards them, so a custom plan means what it always did and the
			// user fills them in.
			const echoTemplate = planHasSegments
				? associatedTreatmentPlan?.treatmentConfigOptions
						?.treatmentConfigTemplates?.[0]
				: undefined;

			setValue(
				"treatmentConfig.visitsPerPeriod",
				echoTemplate?.visitsPerPeriod != null
					? String(echoTemplate.visitsPerPeriod)
					: "",
			);
			setValue(
				"treatmentConfig.minHoursBetweenVisits",
				echoTemplate?.minHoursBetweenVisits != null
					? String(echoTemplate.minHoursBetweenVisits)
					: "",
			);

			// Duration: seeded either way, because it is required and has no editable control on a
			// segmented plan. Reading it from the echo template keeps it acceptable to the guard;
			// off a segmented plan the program's own week count is the sensible starting point.
			const rawPeriodLength = (
				echoTemplate as unknown as TreatmentConfigTemplate | undefined
			)?.periodLength;
			const seededPeriodicity: "weekly" | "monthly" =
				rawPeriodLength === 4.348 ? "monthly" : "weekly";
			const seededWeeks = planHasSegments
				? Number(echoTemplate?.duration) || 0
				: Number(selectedProgram?.parameters?.durationInWeeks) || 0;

			// Store weeks (what the backend expects) and display in the selected unit.
			setValue("treatmentConfig.duration", seededWeeks);
			setIsSettingFromTemplate(false);
			setTreatmentPeriodicity(seededPeriodicity);
			setUserDuration(
				seededPeriodicity === "monthly"
					? Math.round(seededWeeks / 4.348)
					: seededWeeks,
			);

			// A custom plan means "I will choose the properties a template would have given me" — so
			// the fields the enrollment's TreatmentConfig requires but no template is supplying now
			// have to come from here, or Mongoose rejects the whole registration with
			// "Path `name`/`segment`/`range`/`periodLength` is required".
			//
			// `segment` and `range` belong to an older banding feature that nothing reads yet (the
			// admin template editor exposes them as a plain number and free text). Seeded to the
			// single full-programme band so they are valid and obviously unset-by-hand; `periodLength`
			// and `range` are then kept in step with the unit and duration by the effect below.
			setValue("treatmentConfig.name", `custom-${selectedProgram?.name ?? ""}`);
			setValue("treatmentConfig.segment", 1);
			// Undefined, not "" — the guard reads `typeof === "number"`, so an empty string would be
			// a rejected value rather than an absent cap.
			setValue("treatmentConfig.customVisitsDuration", undefined);
		}
	};

	// Get filtered schedule options based on selected template
	const getFilteredScheduleOptions = () => {
		if (
			!associatedTreatmentPlan?.treatmentConfigOptions?.cmVisitScheduleOptions
		) {
			return [];
		}

		// Use local state first, fallback to watched value
		const templateKey = selectedTreatmentConfigTemplate || watchedTemplate;
		const selectedTemplate = treatmentConfigTemplateOptions.find(
			(option) => option.key === templateKey,
		);

		const availableOptions =
			associatedTreatmentPlan.treatmentConfigOptions.cmVisitScheduleOptions;

		// For custom plans, show all schedule options
		if (selectedTemplate?.key === "custom") {
			return availableOptions.map((option) => ({
				key: String(option.cmVisitScheduleOptions), // Use the actual ID as key
				value:
					option.cmVisitScheduleOptionsDaysDisplay ||
					String(option.cmVisitScheduleOptions),
			}));
		}

		// For template plans, show only the schedule options available for this template
		if (selectedTemplate?.data) {
			const templateData = selectedTemplate.data as TreatmentConfigTemplate;

			// Get the IDs of available schedule options for this template
			if (templateData.weeklyVisitsPerPeriod) {
				const availableIds = templateData.cmVisitScheduleOptionIds
					? templateData.cmVisitScheduleOptionIds
							.split(",")
							.map((id: string) => parseInt(id.trim(), 10))
							.filter((id: number) => !isNaN(id))
					: [0, 1]; // fallback to default values

				// Filter options that match the available IDs
				return availableOptions
					.filter((option) =>
						availableIds.includes(option.cmVisitScheduleOptions),
					)
					.map((option) => ({
						key: String(option.cmVisitScheduleOptions), // Use the actual ID as key
						value:
							option.cmVisitScheduleOptionsDaysDisplay ||
							String(option.cmVisitScheduleOptions),
					}));
			}
		}

		return [];
	};

	if (
		isLoadingPlans ||
		isFetchingPlans ||
		isLoadingEvents ||
		isFetchingEvents
	) {
		return null;
	}

	// Only render if the program supports multi-treatment plans
	if (!selectedProgram?.multiTreatmentPlan) {
		return null;
	}

	// Only render if we have an associated treatment plan
	if (!associatedTreatmentPlan) {
		return null; // Simple fallback, no complex UI needed
	}

	return (
		<>
			{/* Template Selection - Inline with Program dropdown */}
			<Controller
				name="treatmentConfigTemplate"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<BasicSelect
						{...field}
						data={treatmentConfigTemplateOptions}
						value={selectedTreatmentConfigTemplate || field.value}
						required={selectedProgram?.multiTreatmentPlan}
						label="Predefined Treatment Plan Template"
						id="beneficiary-treatment-config-template"
						handleChange={(value) => {
							field.onChange(value);
							handleTemplateSelection(value as string);
						}}
						error={!!errors.treatmentConfigTemplate}
						errorMessage={errors.treatmentConfigTemplate?.message as string}
						disabled={
							isEditMode &&
							(treatmentConfigTemplateOptions.length === 0 ||
								!selectedProgram?.treatmentPlanChangeAllowed ||
								enrollment?.status !== EnrollmentStatus.ACTIVE)
						}
					/>
				)}
			/>
			{/* Treatment Plan Fields - Each field as individual grid item */}
			{/* Treatment Plan Period Length */}
			<TextField
				select
				fullWidth
				label="Treatment Plan Period Length"
				value={treatmentPeriodicity}
				onChange={(e) => {
					setTreatmentPeriodicity(e.target.value as "weekly" | "monthly");
					setIsSettingFromTemplate(false); // Reset flag when user manually changes
				}}
				variant="outlined"
				disabled={!areFieldsEditable}
				sx={getFieldStyling()}
			>
				<option value="weekly">Weekly</option>
				<option value="monthly">Monthly</option>
			</TextField>
			{/* Treatment Plan Input Type (Behavior) */}
			<Box sx={{ mt: 1 }}>
				<Controller
					name="treatmentConfig.behavior"
					control={control}
					defaultValue=""
					render={({ field }) => (
						<BasicSelect
							{...field}
							data={
								events?.map((event) => ({
									key: event._id,
									value: event.name,
								})) || []
							}
							value={field.value}
							label="Treatment Plan Input Type"
							id="beneficiary-treatment-behavior"
							handleChange={(value) => {
								field.onChange(value);
							}}
							error={
								!!(errors.treatmentConfig as Record<string, unknown>)?.behavior
							}
							errorMessage={
								(
									(errors.treatmentConfig as Record<string, unknown>)
										?.behavior as { message?: string }
								)?.message || ""
							}
							disabled={!areFieldsEditable}
						/>
					)}
				/>
			</Box>
			{/* Visits Per Period */}
			<Controller
				name="treatmentConfig.visitsPerPeriod"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<TextField
						{...field}
						fullWidth
						label="Visits Per Period"
						variant="outlined"
						type="number"
						disabled={!areFieldsEditable}
						sx={getFieldStyling({ mt: 1 })}
						onChange={(e) => {
							field.onChange(e);
							setValue("treatmentConfig.weeklyVisitsPerPeriod", e.target.value);
						}}
						error={
							!!(errors.treatmentConfig as Record<string, unknown>)
								?.visitsPerPeriod
						}
						helperText={
							(
								(errors.treatmentConfig as Record<string, unknown>)
									?.visitsPerPeriod as { message?: string }
							)?.message || ""
						}
					/>
				)}
			/>
			{/*
			 * Program Duration. Unit-aware and editable ONLY on a custom plan whose treatment plan
			 * has no segments; everywhere else it stays the display-only field it has always been.
			 *
			 * The number shown follows the selected period length — weeks under Weekly, months under
			 * Monthly — while `treatmentConfig.duration` below always stores WEEKS, which is what the
			 * backend expects. Previously the label said "(weeks)" and the value came straight from
			 * `program.parameters.durationInWeeks` regardless of the unit, so Monthly displayed a
			 * week count next to a value stored as months x 4.348.
			 */}
			<TextField
				fullWidth
				label={
					isCustomPlanSelected && treatmentPeriodicity === "monthly"
						? "Program Duration (months)"
						: "Program Duration (weeks)"
				}
				variant="outlined"
				type="number"
				disabled={!areFieldsEditable}
				sx={getFieldStyling()}
				value={
					isCustomPlanSelected
						? userDuration || ""
						: selectedProgram?.parameters?.durationInWeeks || ""
				}
				onChange={(e) => {
					if (!areFieldsEditable) {
						return;
					}
					setUserDuration(Number(e.target.value) || 0);
					setIsSettingFromTemplate(false);
				}}
			/>
			{/* Treatment Config Duration - Hidden but still functional */}
			<Controller
				name="treatmentConfig.duration"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<input
						{...field}
						type="hidden"
						value={userDuration}
						onChange={(e) => {
							const value = Number(e.target.value) || 0;
							setUserDuration(value);
							setIsSettingFromTemplate(false);
						}}
					/>
				)}
			/>
			{/* Min Hours Between Visits - Only show for custom plans */}
			{selectedTreatmentConfigTemplate === "custom" && (
				<Controller
					name="treatmentConfig.minHoursBetweenVisits"
					control={control}
					defaultValue=""
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							label="Min Hours Between Visits"
							variant="outlined"
							type="number"
							disabled={!areFieldsEditable}
							sx={{ mb: 1 }}
						/>
					)}
				/>
			)}
			{/*
			 * Custom Visits Duration — custom plans only.
			 *
			 * A REDUCE-ONLY cap on the enrollment's total expected visits (SIMS-7005, extended to
			 * segmented plans by SIMS-7491 FR-14/AC4). The backend has always supported it —
			 * `validateCustomVisitsDuration` guards it on create and `resolveTotalExpectedVisits`
			 * trims the excess off the LAST segment — but nothing at enrollment time could set it:
			 * it is editable only on the admin treatment-plan form, i.e. as a template value.
			 *
			 * It is NOT one of STRUCTURAL_OVERRIDE_FIELDS, so unlike visitsPerPeriod / minHours /
			 * duration it is legal on a segmented plan; the reduce-only bound is enforced instead.
			 * Sent as a NUMBER (undefined when blank) — the guard checks `typeof === "number"`, so a
			 * string would be rejected as invalid rather than read as an absent cap.
			 */}
			{isCustomPlanSelected && (
				<Controller
					name="treatmentConfig.customVisitsDuration"
					control={control}
					defaultValue=""
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							label="Custom Visits Duration (total visits)"
							variant="outlined"
							type="number"
							disabled={!areFieldsEditable}
							sx={getFieldStyling()}
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(Number(e.target.value) || undefined)
							}
							error={
								!!(errors.treatmentConfig as Record<string, unknown>)
									?.customVisitsDuration
							}
							helperText={
								(
									(errors.treatmentConfig as Record<string, unknown>)
										?.customVisitsDuration as { message?: string }
								)?.message || ""
							}
						/>
					)}
				/>
			)}
			{/*
			 * Reward Engine — custom plans only.
			 *
			 * This is the enrollment-level engine OVERRIDE, so it has to be offered, not merely left
			 * blank: a custom plan exists to choose the properties a template would otherwise supply,
			 * and the engine is one of them. Options come from the plan's own
			 * `rewardEngineIdsOptions`, so only engines the plan actually offers can be picked.
			 *
			 * NOT a structural field, so unlike visitsPerPeriod / minHours / duration this override is
			 * accepted on a segmented plan too (a segment's own engine still wins at resolve time).
			 *
			 * Cleared to `undefined`, never "" — the payload spreads treatmentConfig verbatim and
			 * Mongoose cannot cast an empty string to an ObjectId.
			 */}
			{isCustomPlanSelected && (
				<Controller
					name="treatmentConfig.rewardEngineId"
					control={control}
					defaultValue=""
					render={({ field }) => (
						<BasicSelect
							{...field}
							// No "None" entry: this field is REQUIRED on a custom plan (see
							// beneficiary.schema). Selecting the custom template clears the plan's
							// engine and nothing repopulates it, so an enrollment saved without one
							// resolves `undefined` at visit entry and crashes the page when the
							// rules engine rejects `nameRule: "undefined.json"`. Offering "None"
							// would let the provider re-create exactly that state.
							data={rewardEngineChoices}
							value={(field.value as string) || ""}
							label="Reward Engine"
							id="beneficiary-treatment-reward-engine"
							required
							disabled={!areFieldsEditable}
							handleChange={(value) => field.onChange(value || undefined)}
							error={
								!!(errors.treatmentConfig as Record<string, unknown>)
									?.rewardEngineId
							}
							errorMessage={
								(
									(errors.treatmentConfig as Record<string, unknown>)
										?.rewardEngineId as { message?: string }
								)?.message || ""
							}
						/>
					)}
				/>
			)}
			{/* Visit Schedule - CM Visit Schedule options */}
			{associatedTreatmentPlan?.treatmentConfigOptions
				?.cmVisitScheduleOptions &&
				(selectedTreatmentConfigTemplate || watchedTemplate) && ( // Use either local state or watched value
					<Controller
						name="treatmentConfig.cmVisitSchedule"
						control={control}
						defaultValue=""
						render={({ field }) => {
							const filteredOptions = getFilteredScheduleOptions();
							const availableOptions =
								associatedTreatmentPlan?.treatmentConfigOptions
									?.cmVisitScheduleOptions;

							// Get current value: if it's an object, use its cmVisitScheduleOptions ID
							let currentValue = "";
							if (field.value) {
								if (
									typeof field.value === "object" &&
									field.value.cmVisitScheduleOptions !== undefined
								) {
									currentValue = String(field.value.cmVisitScheduleOptions);
								} else if (typeof field.value === "string") {
									currentValue = field.value;
								}
							}

							return (
								<BasicSelect
									{...field}
									data={filteredOptions}
									value={currentValue}
									label="Visit Schedule"
									id="beneficiary-cm-visit-schedule"
									required={true}
									handleChange={(selectedId) => {
										// Find the schedule option object with matching cmVisitScheduleOptions ID
										const selectedOption = availableOptions?.find(
											(option) =>
												String(option.cmVisitScheduleOptions) === selectedId,
										);
										// Save the full object to the form
										field.onChange(selectedOption || null);
									}}
									error={
										!!(errors.treatmentConfig as Record<string, unknown>)
											?.cmVisitSchedule
									}
									errorMessage={
										(
											(errors.treatmentConfig as Record<string, unknown>)
												?.cmVisitSchedule as { message?: string }
										)?.message || ""
									}
									disabled={
										filteredOptions.length === 0 ||
										(isEditMode &&
											!allowCmVisitScheduleEdit &&
											(treatmentConfigTemplateOptions.length === 0 ||
												!selectedProgram?.treatmentPlanChangeAllowed ||
												enrollment?.status !== EnrollmentStatus.ACTIVE))
									}
								/>
							);
						}}
					/>
				)}
		</>
	);
};

export default TreatmentPlanFields;
